import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { ExpandMore, BugReport, Info } from '@mui/icons-material';
import SkipLogicEngine from '../Common/SkipLogicEngine';

/**
 * Skip Logic Validator Component
 * Helps form creators validate and debug their skip logic rules
 */
export function SkipLogicValidator({ formData, formValues = {} }) {
  const [validationResults, setValidationResults] = useState(null);
  const [summary, setSummary] = useState(null);

  const runValidation = () => {
    if (!formData) {
      setValidationResults([{ type: 'error', message: 'No form data provided' }]);
      return;
    }

    const engine = new SkipLogicEngine(formData, formValues);
    const warnings = engine.validateSkipLogic();
    const summaryData = engine.getSkipLogicSummary();
    
    setValidationResults(warnings);
    setSummary(summaryData);
  };

  const getSeverityColor = (type) => {
    switch (type) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  if (!formData) {
    return (
      <Alert severity="info">
        <AlertTitle>Skip Logic Validator</AlertTitle>
        Load a form to validate its skip logic configuration.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <BugReport />
        <Typography variant="h6">Skip Logic Validator</Typography>
        <Button 
          variant="contained" 
          size="small" 
          onClick={runValidation}
          startIcon={<Info />}
        >
          Validate
        </Button>
      </Box>

      {summary && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">Skip Logic Summary</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Total Fields</TableCell>
                  <TableCell>{summary.totalFields}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Fields with Skip Logic</TableCell>
                  <TableCell>{summary.fieldsWithSkipLogic}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Rules</TableCell>
                  <TableCell>{summary.totalRules}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Field Targets</TableCell>
                  <TableCell>{summary.fieldTargets}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Section Targets</TableCell>
                  <TableCell>{summary.sectionTargets}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <Chip 
                      label={summary.hasSkipLogic ? "Skip Logic Active" : "No Skip Logic"} 
                      color={summary.hasSkipLogic ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {validationResults && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">
              Validation Results ({validationResults.length} issues)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {validationResults.length === 0 ? (
              <Alert severity="success">
                <AlertTitle>All Good!</AlertTitle>
                No skip logic issues found.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {validationResults.map((result, index) => (
                  <Alert key={index} severity={getSeverityColor(result.type)}>
                    <AlertTitle>
                      {result.type === 'error' ? 'Error' : 'Warning'} 
                      {result.field && ` in field ${result.field}`}
                      {result.rule !== undefined && ` (rule ${result.rule + 1})`}
                    </AlertTitle>
                    {result.message}
                  </Alert>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          This validator checks for common skip logic configuration issues like missing targets, 
          invalid values, and self-referencing rules. Run this after configuring skip logic to 
          ensure your form behaves as expected.
        </Typography>
      </Box>
    </Box>
  );
}

export default SkipLogicValidator;
